import { useMemo, useState } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { ElementRef } from "../model/inp/properties.ts";
import type { SimulationResults } from "../model/results/simulationResults.ts";
import { seriesFor, unitLabelFor, variablesFor } from "../model/results/resultsVariables.ts";
import type { TextKey } from "../i18n/en.ts";
import { useLanguage, useT } from "../i18n/hooks.ts";
import { localeFor } from "../i18n/language.ts";
import { formatNumber } from "../i18n/formatNumber.ts";
import { ResultsDashboardMiniChart } from "./ResultsDashboardMiniChart.tsx";
import type { ResultsDashboardLayout, ResultsDashboardView } from "./useResultsDashboard.ts";
import { CloseIcon } from "./CloseIcon.tsx";
import {
  CHART_AXIS_TICK,
  CHART_GRID_PROPS,
  CHART_PALETTE,
  CHART_TOOLTIP_STYLE,
} from "./chartTheme.ts";

// Deliberately not NODE_COLOR/LINK_COLOR (mapTheme.ts): those don't even
// vary by kind any more (UI redesign phase 2 — shape/dash carries kind now,
// see that module's own header), and wouldn't help here regardless, since
// two selected junctions would still render identically. This cycles by
// position instead, so each selected element gets a distinct color
// regardless of kind.
//
// Palette lives in `chartTheme.ts` as `CHART_PALETTE` — same Okabe-Ito set
// every other chart in the app uses (second-audit finding: it was declared
// locally here while other charts hardcoded an ad-hoc blue).
function colorFor(index: number): string {
  return CHART_PALETTE[index % CHART_PALETTE.length];
}

function categoryLabelKey(category: ElementRef["category"]): TextKey {
  if (category === "node") return "elementBrowser.nodes";
  if (category === "link") return "elementBrowser.links";
  if (category === "subcatchment") return "elementBrowser.subcatchments";
  return "elementBrowser.raingages";
}

export function ResultsDashboardCategoryBlock({
  category,
  elements,
  results,
  flowUnits,
  layout,
  view,
  onRemoveElement,
}: {
  category: ElementRef["category"];
  elements: ElementRef[];
  results: SimulationResults;
  flowUnits: string;
  layout: ResultsDashboardLayout;
  view: ResultsDashboardView;
  onRemoveElement: (ref: ElementRef) => void;
}) {
  const t = useT();
  const language = useLanguage();
  const variables = variablesFor(category);
  const [variableKey, setVariableKey] = useState(variables[0]?.key);
  const activeKey = variables.some((v) => v.key === variableKey) ? variableKey : variables[0]?.key;

  // Computed before the early returns below — every hook must run on
  // every render (Rules of Hooks).
  const resolved = useMemo(() => {
    if (!activeKey) return [];
    const rows: { el: ElementRef; series: { labelKey: TextKey; values: number[] } }[] = [];
    for (const el of elements) {
      const series = seriesFor(results, el, activeKey);
      if (series) rows.push({ el, series });
    }
    return rows;
  }, [elements, results, activeKey]);

  const timeLabels = useMemo(
    () =>
      results.times.map((time) =>
        time.toLocaleString(localeFor(language), {
          month: "short", // not "numeric" — "1/6" reads as ambiguous
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          timeZone: "UTC", // simulation timestamps are UTC-anchored, not local
        })
      ),
    [results.times, language]
  );

  const combinedData = useMemo(
    () =>
      timeLabels.map((time, i) => {
        const row: Record<string, string | number> = { time };
        for (const { el, series } of resolved) row[el.name] = series.values[i];
        return row;
      }),
    [timeLabels, resolved]
  );

  if (!activeKey) return null; // raingage: no variables at all
  if (resolved.length === 0) return null;

  const unitLabel = unitLabelFor(activeKey, flowUnits);
  const title = unitLabel
    ? `${t(resolved[0].series.labelKey)} (${unitLabel})`
    : t(resolved[0].series.labelKey);

  return (
    <section className="results-dashboard-category-block">
      <div className="results-dashboard-category-header">
        <div>
          <h3>{t(categoryLabelKey(category))}</h3>
          <span className="results-dashboard-category-subtitle">{title}</span>
        </div>
        {variables.length > 1 && (
          <div className="results-chart-view-toggle results-chart-variable-toggle" role="tablist">
            {variables.map((v) => (
              <button
                key={v.key}
                className={activeKey === v.key ? "button tab-active" : "button"}
                role="tab"
                aria-selected={activeKey === v.key}
                onClick={() => setVariableKey(v.key)}
              >
                {t(v.labelKey)}
              </button>
            ))}
          </div>
        )}
      </div>
      {/* Iterates elements, not resolved: an element with no series for
          the active variable (e.g. a JUNCTION while "Volume" is active)
          must stay removable, not vanish from the list. */}
      <ul className="results-dashboard-chips">
        {elements.map((el) => (
          <li key={el.name} className="results-dashboard-chip">
            <span className="results-dashboard-chip-name">{el.name}</span>
            <button
              className="button-icon results-dashboard-chip-remove"
              onClick={() => onRemoveElement(el)}
              aria-label={t("resultsDashboard.remove")}
              title={t("resultsDashboard.remove")}
            >
              <CloseIcon />
            </button>
          </li>
        ))}
      </ul>
      {view === "table" ? (
        <div className="results-chart-table-wrap">
          <table className="named-collection-points-table">
            <thead>
              <tr>
                <th>{t("results.time")}</th>
                {resolved.map(({ el }, i) => (
                  <th key={el.name} style={{ color: colorFor(i) }}>
                    {el.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {combinedData.map((row, i) => (
                <tr key={i}>
                  <td className="pattern-table-label-cell">{row.time}</td>
                  {resolved.map(({ el }) => {
                    const value = row[el.name] as number;
                    return (
                      <td key={el.name} className="pattern-table-label-cell">
                        {Number.isFinite(value) ? formatNumber(value, language, 3) : "—"}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : layout === "combined" ? (
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={combinedData} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
            <CartesianGrid {...CHART_GRID_PROPS} />
            <XAxis dataKey="time" tick={CHART_AXIS_TICK} minTickGap={30} />
            <YAxis
              tick={CHART_AXIS_TICK}
              label={
                unitLabel
                  ? { value: unitLabel, angle: -90, position: "insideLeft", fontSize: 11 }
                  : undefined
              }
            />
            <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
            <Legend wrapperStyle={{ fontSize: "0.75rem" }} />
            {resolved.map(({ el }, i) => (
              <Line
                key={el.name}
                type="monotone"
                dataKey={el.name}
                stroke={colorFor(i)}
                dot={false}
                strokeWidth={2}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <div className="results-dashboard-grid">
          {resolved.map(({ el, series }, i) => (
            <div key={el.name} className="results-dashboard-grid-item">
              <h4 style={{ color: colorFor(i) }}>{el.name}</h4>
              <ResultsDashboardMiniChart
                data={timeLabels.map((time, j) => ({ time, value: series.values[j] }))}
                height={140}
              />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
